import { formatToDollar, type SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { type FC, type JSX } from "react";
import { formatEmpty } from "../../../utils";
import { Input } from "../../form";
import { cn } from "../../lib/utils";
import { EditableMetricCard } from "../../patterns/metric-card";
import { useEditableBLOCSize } from "../hooks/useEditableBLOCSize";
import { FinancialPeriodBundle } from "../hooks/useFinancialsMetrics";

export type BLOCSizeCardProps = {
	accountId: SuretyAccountId;
	recentFYE?: FinancialPeriodBundle;
	canEdit?: boolean;
};

type BLOCDisplayProps = {
	val: Option<number>;
	label: string;
};

const BLOCDisplay: FC<BLOCDisplayProps> = ({ val, label }): JSX.Element => (
	<div className="flex-1 flex flex-col">
		<span className="text-[10px] font-bold text-secondary">{label}</span>
		<span className="text-xs font-semibold">{formatEmpty(formatToDollar(val))}</span>
	</div>
);

export const BLOCSizeCard: FC<BLOCSizeCardProps> = ({ accountId, recentFYE, canEdit }) => {
	const testId = "bloc-size-card";
	const {
		isEditing,
		updatedBLOCSize,
		displayBLOCSize,
		mutationError,
		startEditing,
		cancelEditing,
		changeBLOCSize,
		submit,
		dismissError,
		canSubmit,
	} = useEditableBLOCSize({
		accountId,
		blocSize: recentFYE?.blocSize,
	});
	const showEditing = isEditing && canEdit;

	const blocSize = updatedBLOCSize ?? displayBLOCSize;
	const blocUsage = recentFYE?.balanceSheet?.view?.liabilities.current.blocBalance.total.allowed;
	const statementDate = recentFYE?.period.periodEndDate
		? DateTime.fromISO(recentFYE.period.periodEndDate).toFormat("MM/dd/yy")
		: null;
	const utilization =
		Option.isSome(blocUsage) && Option.isSome(blocSize) && blocSize !== 0
			? Math.round((blocUsage / blocSize) * 100)
			: null;

	return (
		<EditableMetricCard
			testId={testId}
			label="BLOC"
			className="gap-1"
			readOnly={!canEdit}
			errorHeading="Update BLOC Size Error"
			confirmTooltipId="valid-bloc-size-tooltip"
			confirmTooltipContent="BLOC Size must be a non-negative number"
			controller={{
				isEditing,
				canSubmit,
				mutationError,
				startEditing,
				cancelEditing,
				submit,
				dismissError,
			}}
		>
			<EditableMetricCard.ValueRow data-testid={`${testId}-display`}>
				{showEditing ? (
					<div className="flex flex-col gap-2">
						<span className="text-[10px] font-bold text-secondary">Size</span>
						<div className="relative flex-1 min-w-0">
							<span className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary text-sm pointer-events-none z-10">
								$
							</span>
							<Input
								testId={`${testId}-input`}
								type="number"
								value={blocSize ?? ""}
								onChange={(e) => {
									const val = e.target.value === "" ? null : Number(e.target.value);
									changeBLOCSize(val);
								}}
								containerClassName="h-[28px] flex-1 min-w-0"
								className="h-[28px] w-full bg-surface-light ml-5 focus:outline-none focus:border-bc-focus text-sm py-0 pl-1 pr-1 rounded [&[type=number]]:appearance-textfield [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
							/>
						</div>
					</div>
				) : (
					<div className="flex items-baseline gap-4 w-full">
						<BLOCDisplay val={blocUsage} label="Usage" />
						<BLOCDisplay val={blocSize} label="Size" />
					</div>
				)}
			</EditableMetricCard.ValueRow>
			{!showEditing && utilization !== null && (
				<EditableMetricCard.Caption
					className={cn("text-xs text-secondary", utilization > 100 && "text-danger")}
				>
					{`${utilization}% utilization ${statementDate ? `as of ${statementDate}` : ""}`}
				</EditableMetricCard.Caption>
			)}
		</EditableMetricCard>
	);
};
